import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/common";
import { careFileUrl } from "@/lib/care";
import { AnStatusBadge, AN_STATUS_META } from "@/lib/sav";
import { fmtDate, fmtDateTime } from "@/lib/helpers";
import * as Icons from "lucide-react";

export function AnomalyList() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("open");
  const [data, setData] = useState(null);
  const [due, setDue] = useState([]);
  const load = useCallback(() => {
    setData(null);
    api.get("/care/anomalies", { params: status === "open" ? { open_only: true } : status === "all" ? {} : { status } })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], counts: {} }));
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/care/warranties/due").then((r) => setDue(r.data || [])).catch(() => {}); }, []);
  const counts = data?.counts || {};
  const openCount = ["nouveau", "analyse", "planifiee", "en_intervention"].reduce((s, k) => s + (counts[k] || 0), 0);
  const chips = [["open", `À traiter (${openCount})`], ...Object.entries(AN_STATUS_META).map(([k, m]) => [k, `${m.label} (${counts[k] || 0})`]), ["all", "Tous"]];

  return (
    <div className="space-y-4" data-testid="anomaly-list">
      {due.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/60 p-3" data-testid="warranty-due-alert">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-800"><Icons.ShieldAlert className="h-4 w-4" />{due.length} garantie(s) arrivant à échéance</p>
          <ul className="mt-1 space-y-0.5 text-sm">{due.map((w) => (
            <li key={w.id}><button onClick={() => navigate(`/care/${w.equipment_id}`)} className="text-amber-900 hover:underline">{w.equipment} — {w.kind_label} jusqu'au {fmtDate(w.end_date)}</button></li>))}</ul>
        </Card>
      )}
      <div className="flex flex-wrap gap-2">
        {chips.map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)} data-testid={`an-filter-${k}`}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${status === k ? "bg-[#0D2E24] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
      {!data ? <Card className="h-24 animate-pulse bg-slate-100" />
        : data.items.length === 0 ? <EmptyState icon="TriangleAlert" title="Aucun signalement" testId="an-empty">Aucune anomalie dans cette catégorie.</EmptyState>
        : (
          <div className="space-y-2">
            {data.items.map((a) => (
              <Card key={a.id} onClick={() => navigate(`/care/anomalies/${a.id}`)} className="flex cursor-pointer gap-3 p-3 transition-shadow hover:shadow-md" data-testid={`an-row-${a.id}`}>
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100 text-slate-300">
                  {a.first_photo_id ? <img src={careFileUrl(a.first_photo_id)} alt="" className="h-full w-full object-cover" /> : <Icons.TriangleAlert className="h-6 w-6" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono-data text-xs text-slate-400">{a.reference}</span>
                    <AnStatusBadge status={a.status} />
                    {a.team_unread && a.status !== "nouveau" && <span className="rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-bold text-white">Nouvelle précision</span>}
                  </div>
                  <p className="mt-0.5 truncate text-sm font-semibold text-slate-800">{a.type_label} · {a.equipment_reference} {a.equipment_name}</p>
                  <p className="truncate text-xs text-slate-500">{a.client_name} — {a.site_name}</p>
                  <p className="line-clamp-1 text-xs text-slate-600">{a.description}</p>
                </div>
                <div className="hidden shrink-0 text-right text-xs text-slate-500 sm:block">
                  <p>Signalé le {fmtDateTime(a.created_at)}</p>
                  <p>{a.assignee_name ? `Responsable : ${a.assignee_name}` : "Non affecté"}</p>
                  {a.planned_start && <p className="font-medium text-sky-700">Intervention : {fmtDate(a.planned_start)}{a.planned_time ? ` ${a.planned_time}` : ""}</p>}
                </div>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}
